<template>
  <div class="d-contents">
    <v-skeleton-loader
      v-if="tenderListCreation || (tenderListsInfoLoading && !(currentList && offers))"
      type="table-tbody"
    />
    <NoOffersPlug v-else-if="!(currentList && offers)" @action="handleInvitation" />
    <div v-else :id="TABLES_IDS.TENDER_TABLE_ID" class="tender-list-wrapper">
      <!--    :key используется для переотрисовки таблицы при смене версий, так как данные -->
      <!--    неоднородны и не провоцируют обновления значений при изменении выбранной версии -->
      <div
        :key="versionUpdated + refreshTenderTableTrigger"
        class="tender-list"
        :style="{
          'grid-template-columns': dynamicColumnsCount
            ? `420px 88px 140px repeat(${
                dynamicColumnsCount + GENERAL_ESTIMATE_COLUMN_WIDTH
              }, 130px)`
            : `420px 88px 140px repeat(${GENERAL_ESTIMATE_COLUMN_WIDTH}, 130px)`,
        }"
      >
        <div class="d-contents tender-list-header">
          <TenderListFullHeightHeaderCell class="tender-list-cell_description left-sticky-cell">
            {{ $t('Description of work') }}
          </TenderListFullHeightHeaderCell>
          <TenderListFullHeightHeaderCell class="tender-list-cell_unit">
            {{ $t('Unit') }}
          </TenderListFullHeightHeaderCell>
          <TenderListFullHeightHeaderCell class="tender-list-cell_end">
            {{ $t('Quantity') }}
          </TenderListFullHeightHeaderCell>
          <TenderListLowHeaderCell
            class="tender-list-cell_row tender-list-cell_top-header left-border right-border"
            :style="{
              'grid-area': `${HEADER_ROW_START} / ${GENERAL_ESTIMATE_COLUMN_START} / ${HEADER_ROW_CENTER} / ${DYNAMIC_COLUMNS_START}`,
            }"
          >
            <div class="flex-row spacex-6">
              <span class="grey-900">{{ $t('General estimate') }}</span>
              <InfoTooltip>
                {{
                  $t(
                    'In this column you can transfer one of the price options from any offer. You can also specify your own price without linking it to a contractor.'
                  )
                }}
              </InfoTooltip>
            </div>
            <TenderListGeneralVersionSelector
              :offers="offers"
              @change-general-selected-version="copyAllPricesToProjectHandler"
            />
          </TenderListLowHeaderCell>
          <TenderListLowHeaderCell
            :style="{
              'grid-area': `${HEADER_ROW_CENTER} / ${GENERAL_ESTIMATE_COLUMN_START} /
              ${HEADER_ROW_END} / ${GENERAL_ESTIMATE_COLUMN_START + 1}`,
            }"
            class="top-36 left-border"
          >
            <span>{{ $t('Labour') }}, {{ currencySymbol(projectCurrency) }}</span>
          </TenderListLowHeaderCell>
          <TenderListLowHeaderCell
            :style="{
              'grid-area': `${HEADER_ROW_CENTER} / ${GENERAL_ESTIMATE_COLUMN_START + 1} /
              ${HEADER_ROW_END} / ${GENERAL_ESTIMATE_COLUMN_START + 2}`,
            }"
            class="top-36"
          >
            <span>{{ $t('Materials') }}, {{ currencySymbol(projectCurrency) }}</span>
          </TenderListLowHeaderCell>
          <TenderListLowHeaderCell
            :style="{
              'grid-area': `${HEADER_ROW_CENTER} / ${GENERAL_ESTIMATE_COLUMN_START + 2} /
              ${HEADER_ROW_END} / ${GENERAL_ESTIMATE_COLUMN_START + 3}`,
            }"
            class="top-36"
          >
            <span>{{ $t('Tools') }}, {{ currencySymbol(projectCurrency) }}</span>
          </TenderListLowHeaderCell>
          <TenderListLowHeaderCell
            :style="{
              'grid-area': `${HEADER_ROW_CENTER} / ${GENERAL_ESTIMATE_COLUMN_START + 3} /
              ${HEADER_ROW_END} / ${GENERAL_ESTIMATE_COLUMN_START + 4}`,
            }"
            class="top-36 right-border"
          >
            <span>{{ $t('Total') }}, {{ currencySymbol(projectCurrency) }}</span>
          </TenderListLowHeaderCell>
          <div v-for="(offer, index) in offers" :key="offer.id" class="d-contents">
            <div
              class="tender-list-cell tender-list-cell_title tender-list-cell_low-header-cell tender-list-cell_version right-border"
              :style="{
                'grid-area': `${HEADER_ROW_START} / ${nameColumnStart(index)} /
                  ${HEADER_ROW_END} / ${nameColumnEnd(index)}`,
              }"
            >
              <TenderListOfferVersionSelector
                :selected-version="selectedVersions[offer.id] || {}"
                :offer="offer"
                @changeSelectedVersion="changeSelectedVersion"
              />
            </div>
            <TenderListLowHeaderCell
              class="tender-list-cell_action top-36"
              :style="getHeaderGridByPositionAndIndex(index, 1)"
            >
              <span>{{ $t('Per unit') }}, {{ currencySymbol(projectCurrency) }}</span>
              <ExpandButton
                v-if="!openedColumns.includes(index)"
                :is-open="false"
                @click="toggleColumn(index)"
              />
            </TenderListLowHeaderCell>
            <div v-if="openedColumns.includes(index)" class="d-contents">
              <TenderListLowHeaderCell
                class="top-36"
                :style="getHeaderGridByPositionAndIndex(index, 2)"
              >
                <span>{{ $t('Labour') }} ≈ {{ currencySymbol(projectCurrency) }}</span>
              </TenderListLowHeaderCell>
              <TenderListLowHeaderCell
                class="top-36"
                :style="getHeaderGridByPositionAndIndex(index, 3)"
              >
                <span>{{ $t('Materials') }} ≈ {{ currencySymbol(projectCurrency) }}</span>
              </TenderListLowHeaderCell>
              <TenderListLowHeaderCell
                class="top-36"
                :style="getHeaderGridByPositionAndIndex(index, 4)"
              >
                <span>{{ $t('Tools') }} ≈ {{ currencySymbol(projectCurrency) }}</span>
              </TenderListLowHeaderCell>
            </div>
            <TenderListLowHeaderCell
              class="tender-list-cell_action spacex-8 top-36 right-border"
              :style="{
                'grid-area': `${HEADER_ROW_CENTER} / ${totalStart(index)} /
                ${HEADER_ROW_END} / ${totalStart(index) + 1}`,
              }"
            >
              <ExpandButton
                v-if="openedColumns.includes(index)"
                :is-open="true"
                @click="toggleColumn(index)"
              />
              <span>{{ $t('Total') }}, {{ currencySymbol(projectCurrency) }}</span>
            </TenderListLowHeaderCell>
          </div>
        </div>
        <div class="d-contents tender-list-table">
          <div
            v-for="(row, rowIndex) in currentRows"
            :key="row.id"
            class="d-contents tender-list-row"
            :class="{
              'tender-list__parent-row': row.row.group_level,
              'not-display': hiddenRows.includes(row.id),
            }"
            @click="toggleNestedRows(row, computedRows)"
          >
            <div
              class="tender-list-cell tender-list-cell_breakdown"
              :class="{
                'tender-list-cell_hidden': !openedBreakdownRowIds.includes(row.id),
              }"
              :style="breakdownGridLocation(rowIndex)"
            >
              <TenderListBreakdown
                v-if="openedBreakdownRowIds.includes(row.id)"
                :currency="tenderInfo.currency"
                :breakdown-rows="row.breakdownRows"
                :parent-row="row"
                :opened-columns="openedColumns"
                :offers="offers"
                :selected-version="selectedVersions"
                :parent-row-index="rowIndex"
                @toggle-column="(index) => toggleColumn(index)"
              />
            </div>
            <div
              class="tender-list-cell tender-list-cell_description left-sticky-cell"
              :style="positionGridRow(rowIndex)"
            >
              <div class="tender-list-cell_description-main spacex-8">
                <div class="tender-list-cell_group-no">{{ row.row.groupNo }}</div>
                <div class="flex-column">
                  <span class="tender-list-cell_description-title">
                    {{ row.row.description_of_works }}
                  </span>
                </div>
              </div>
              <Button2
                :id="`comment-${row.row.id}`"
                class="tender-list-cell_open-comment"
                type="func"
                icon="maximize"
                @click="openComments(row.row, row.row.id, row.offer_rows)"
              />
            </div>
            <div class="tender-list-cell tender-list-cell_unit" :style="positionGridRow(rowIndex)">
              <span v-if="!row.row.group_level && row.row.unit">
                {{ unitName(row.row.unit.name) }}
              </span>
            </div>
            <div class="tender-list-cell tender-list-cell_value" :style="positionGridRow(rowIndex)">
              <span v-if="!row.row.group_level">{{ addSpaces(row.row.quantity) }}</span>
            </div>
            <div class="d-contents">
              <div :style="getGeneralEstimatePosition(rowIndex, 0)" class="left-border">
                <TenderListGeneralEstimateCell
                  :currency="tenderInfo.currency"
                  :value="row.row.labour_total"
                  :row="row"
                  field="labour_total"
                  :offers="offers"
                  :content-editable="true"
                  :order-of-rows-ids="orderOfRowsIds"
                  @edit-cell-value="editGeneralEstimateValueHandler"
                  @change-general-selected-version="
                    (info) =>
                      copyCellPricesToProject({
                        versionId: info.id,
                        column: 'labour',
                        projectRowId: row.row.id,
                        versionIds: getVersionIds(),
                      })
                  "
                />
              </div>
              <TenderListGeneralEstimateCell
                :currency="tenderInfo.currency"
                :grid-style="getGeneralEstimatePosition(rowIndex, 1)"
                :value="row.row.materials_total"
                :row="row"
                field="materials_total"
                :offers="offers"
                :content-editable="true"
                :order-of-rows-ids="orderOfRowsIds"
                @edit-cell-value="editGeneralEstimateValueHandler"
                @change-general-selected-version="
                  (info) =>
                    copyCellPricesToProject({
                      versionId: info.id,
                      column: 'materials',
                      projectRowId: row.row.id,
                      versionIds: getVersionIds(),
                    })
                "
              />
              <TenderListGeneralEstimateCell
                :currency="tenderInfo.currency"
                :grid-style="getGeneralEstimatePosition(rowIndex, 2)"
                :value="row.row.tools_total"
                :row="row"
                field="tools_total"
                :offers="offers"
                :content-editable="true"
                :order-of-rows-ids="orderOfRowsIds"
                @edit-cell-value="editGeneralEstimateValueHandler"
                @change-general-selected-version="
                  (info) =>
                    copyCellPricesToProject({
                      versionId: info.id,
                      column: 'tools',
                      projectRowId: row.row.id,
                      versionIds: getVersionIds(),
                    })
                "
              />
              <div class="right-border" :style="getGeneralEstimatePosition(rowIndex, 3)">
                <TenderListGeneralEstimateCell
                  :currency="tenderInfo.currency"
                  class="right-border"
                  :value="row.row.total_total"
                  :row="row"
                  field="total_total"
                  :offers="offers"
                  :content-editable="false"
                />
              </div>
            </div>
            <div v-for="(offer, index) in offers" :key="offer.id" class="d-contents">
              <TenderListCell
                :currency="tenderInfo.currency"
                :style="getBodyGridByPositionAndIndex(index, 1, rowIndex)"
                field="total"
                :index="index"
                :row="row"
                :offers="offers"
                :selected-versions="selectedVersions"
              />
              <div v-if="openedColumns.includes(index)" class="d-contents">
                <draggable
                  :style="getBodyGridByPositionAndIndex(index, 2, rowIndex)"
                  v-bind="dragOptions"
                  :group="draggableParamsGroup"
                  :handle="row.row.group_level"
                  @start="dragStartHandler"
                  @end="
                    (e) =>
                      dragEndHandler(e, offer.id, {
                        column: 'labour',
                        projectRowId: row.row.id,
                        versionIds: getVersionIds(),
                      })
                  "
                >
                  <TenderListCell
                    :currency="tenderInfo.currency"
                    class="pointer"
                    field="labour_total"
                    percent-field="labour_extra_percent"
                    :index="index"
                    :row="row"
                    :offers="offers"
                    :selected-versions="selectedVersions"
                  />
                </draggable>
                <draggable
                  :style="getBodyGridByPositionAndIndex(index, 3, rowIndex)"
                  v-bind="dragOptions"
                  :group="draggableParamsGroup"
                  :handle="row.row.group_level"
                  @start="dragStartHandler"
                  @end="
                    (e) =>
                      dragEndHandler(e, offer.id, {
                        column: 'materials',
                        projectRowId: row.row.id,
                        versionIds: getVersionIds(),
                      })
                  "
                >
                  <TenderListCell
                    :currency="tenderInfo.currency"
                    class="pointer"
                    field="materials_total"
                    percent-field="materials_extra_percent"
                    :index="index"
                    :row="row"
                    :offers="offers"
                    :selected-versions="selectedVersions"
                  />
                </draggable>
                <draggable
                  :style="getBodyGridByPositionAndIndex(index, 4, rowIndex)"
                  v-bind="dragOptions"
                  :group="draggableParamsGroup"
                  :handle="row.row.group_level"
                  @start="dragStartHandler"
                  @end="
                    (e) =>
                      dragEndHandler(e, offer.id, {
                        column: 'tools',
                        projectRowId: row.row.id,
                        versionIds: getVersionIds(),
                      })
                  "
                >
                  <TenderListCell
                    :currency="tenderInfo.currency"
                    class="pointer"
                    field="tools_total"
                    percent-field="tools_extra_percent"
                    :index="index"
                    :row="row"
                    :offers="offers"
                    :selected-versions="selectedVersions"
                  />
                </draggable>
              </div>
              <draggable
                :style="getTotalFieldGrid(index, rowIndex)"
                v-bind="dragOptions"
                :group="draggableParamsGroup"
                :handle="row.row.group_level"
                @start="dragStartHandler"
                @end="
                  (e) =>
                    dragEndHandler(
                      e,
                      offer.id,
                      {
                        projectRowId: row.row.id,
                        versionIds: getVersionIds(),
                      },
                      true
                    )
                "
              >
                <TenderListCell
                  :currency="tenderInfo.currency"
                  class="right-border pointer"
                  field="total_total"
                  percent-field="extra_percent"
                  need-green-backlight
                  :index="index"
                  :row="row"
                  :offers="offers"
                  :selected-versions="selectedVersions"
                />
              </draggable>
            </div>
          </div>
        </div>
        <draggable :style="getGeneralEstimateDropZoneGrid" :group="draggableParamsGroup">
          <TenderListGeneralEstimateDrop v-if="drag" :id="GENERAL_ESTIMATE_DROP_ZONE_ID" />
        </draggable>
        <div class="d-contents">
          <div
            class="tender-list-cell tender-list-cell_footer tender-list-cell_footer-description left-sticky-cell"
            :style="footerGridRow"
          >
            <span>{{ $t('Total price') }}</span>
          </div>
          <div
            class="tender-list-cell tender-list-cell_footer tender-list-cell_unit"
            :style="footerGridRow"
          />
          <div class="tender-list-cell tender-list-cell_footer" :style="footerGridRow" />
          <TenderListFooterCell
            :currency-symbol="currencySymbol(projectCurrency)"
            class="left-border"
            :style="footerGridRow"
            :value="get(currentList, 'project_rows_sums.labour_total', '')"
          />
          <TenderListFooterCell
            :currency-symbol="currencySymbol(projectCurrency)"
            :style="footerGridRow"
            :value="get(currentList, 'project_rows_sums.materials_total', '')"
          />
          <TenderListFooterCell
            :currency-symbol="currencySymbol(projectCurrency)"
            :style="footerGridRow"
            :value="get(currentList, 'project_rows_sums.tools_total', '')"
          />
          <TenderListFooterCell
            :currency-symbol="currencySymbol(projectCurrency)"
            class="right-border"
            :style="footerGridRow"
            :value="get(currentList, 'project_rows_sums.total_total', '')"
          />
          <div v-for="version in selectedVersions" :key="version.versionId" class="d-contents">
            <div class="d-contents">
              <TenderListFooterCell
                :currency-symbol="currencySymbol(projectCurrency)"
                :style="getFooterGridByPositionAndIndex(version.versionPriorityIndex, 1)"
                :value="get(currentList, `sums[${version.versionId}].total`, '')"
              />
            </div>
            <div v-if="openedColumns.includes(version.versionPriorityIndex)" class="d-contents">
              <TenderListFooterCell
                :currency-symbol="currencySymbol(projectCurrency)"
                :style="getFooterGridByPositionAndIndex(version.versionPriorityIndex, 2)"
                :value="get(currentList, `sums[${version.versionId}].labour_total`, '')"
              />
              <TenderListFooterCell
                :currency-symbol="currencySymbol(projectCurrency)"
                :style="getFooterGridByPositionAndIndex(version.versionPriorityIndex, 3)"
                :value="get(currentList, `sums[${version.versionId}].materials_total`, '')"
              />
              <TenderListFooterCell
                :currency-symbol="currencySymbol(projectCurrency)"
                :style="getFooterGridByPositionAndIndex(version.versionPriorityIndex, 4)"
                :value="get(currentList, `sums[${version.versionId}].tools_total`, '')"
              />
            </div>
            <TenderListFooterCell
              :currency-symbol="currencySymbol(projectCurrency)"
              class="right-border"
              :style="getFooterTotalGridByPositionAndIndex(version.versionPriorityIndex)"
              :value="get(currentList, `sums[${version.versionId}].total_total`, '')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import router from '@/router';
  import { mapActions, mapGetters, mapState } from 'vuex';
  import { VSkeletonLoader } from 'vuetify/lib';
  import ExpandButton from '@/components/tender/tender-list/ExpandButton';
  import nestedRowsStructure from '@/utils/mixins/nested-rows-structure';
  import TenderListCell from '@/containers/tender/tender-list/TenderListCell';
  import TenderPositionCard from '@/containers/tender/position/TenderPositionCard';
  import get from 'lodash.get';
  import TenderListOfferVersionSelector from '@/containers/tender/tender-list/TenderListOfferVersionSelector';
  import {
    DEFAULT_COLUMN_WIDTH,
    DYNAMIC_COLUMNS_START,
    FIRST_DYNAMIC_ROW_END,
    HEADER_ROW_CENTER,
    HEADER_ROW_END,
    HEADER_ROW_START,
    WIDE_COLUMN_DELTA,
    BREAKDOWN_COEFFICIENT,
    GENERAL_ESTIMATE_COLUMN_START,
    GENERAL_ESTIMATE_COLUMN_WIDTH,
  } from '@/containers/tender/tender-list/constants';
  import TABLES_IDS from '@/utils/constants/tables-ids';
  import TenderListBreakdown from '@/containers/tender/tender-list/TenderListBreakdown';
  import { changeQuery, deleteQuery } from '@/utils/router';
  import TenderListGeneralEstimateCell from '@/containers/tender/tender-list/TenderListGeneralEstimateCell';
  import TenderListGeneralVersionSelector from '@/containers/tender/tender-list/TenderListGeneralVersionSelector';
  import InfoTooltip from '@/components/ui/InfoTooltip';
  import TenderListFooterCell from '@/containers/tender/tender-list/TenderListFooterCell';
  import addSpaces from '@/utils/formatting/add-spaces';
  import TenderListLowHeaderCell from '@/components/tender/tender-list/TenderListLowHeaderCell';
  import TenderListFullHeightHeaderCell from '@/components/tender/tender-list/TenderListFullHeightHeaderCell';
  import draggable from 'vuedraggable';
  import TenderListGeneralEstimateDrop from '@/components/tender/tender-list/TenderListGeneralEstimateDrop';
  import unitName from '@/utils/mixins/get-unit-name';
  import Button2 from '@/components/ui-v2/Button';
  import editTender from '@/utils/mixins/edit-tender';
  import NoOffersPlug from '@/containers/tender/plugs/NoOffersPlug';

  export default {
    name: 'TenderList',
    components: {
      TenderListGeneralEstimateDrop,
      draggable,
      TenderListFullHeightHeaderCell,
      TenderListLowHeaderCell,
      TenderListFooterCell,
      InfoTooltip,
      TenderListGeneralVersionSelector,
      TenderListGeneralEstimateCell,
      TenderListBreakdown,
      TenderListCell,
      ExpandButton,
      TenderListOfferVersionSelector,
      VSkeletonLoader,
      Button2,
      NoOffersPlug,
    },
    mixins: [nestedRowsStructure, unitName, editTender],
    data() {
      return {
        drag: false,
        draggableParamsGroup: { name: 'general-estimate', put: () => false },
        tenderListId: router.currentRoute.params.tenderListId,
        // Example value: [...индексы раскрытых столбцов], [1,0]
        openedColumns: [],
        // Example value:
        // {offerId: { versionIndex, versionId},
        // {"9": {versionIndex: 0, versionId: 12},"10":{versionIndex: 0, versionId: 12}}
        selectedVersions: {},
        // Example value:
        // {id строки родителя:{"show":true,"children":[...id дочерних строк]},
        // {"50":{"show":true,"children":[52,53,54]},"55":{"show":true,"children":[56,57]}}
        nestedRowsStructure: null,
        // Example value: [...id скрытых строк], [56,57,52,53,54]
        hiddenRows: [],
        versionUpdated: false,
        openedBreakdownRowIds: [],
        GENERAL_ESTIMATE_DROP_ZONE_ID: 'tender-list-drop-zone',
        DYNAMIC_COLUMNS_START,
        DEFAULT_COLUMN_WIDTH,
        WIDE_COLUMN_DELTA,
        HEADER_ROW_END,
        HEADER_ROW_CENTER,
        HEADER_ROW_START,
        FIRST_DYNAMIC_ROW_END,
        BREAKDOWN_COEFFICIENT,
        GENERAL_ESTIMATE_COLUMN_WIDTH,
        GENERAL_ESTIMATE_COLUMN_START,
        TABLES_IDS,
        versionsHelper: true, // should versions be updated
        currentRows: [],
        tenderListCreation: false,
      };
    },
    async created() {
      this.tenderListCreation = true;
      this.openCommentsByQuery();
      await this.fillSelectedVersions();

      let unwatch = this.$watch('offers', (newVal) => {
        if (newVal) {
          this.fillSelectedVersions();
          unwatch();
        }
      });
      this.tenderListCreation = false;
    },
    watch: {
      // Если знаешь как норм тригериться на обновления параметра урла (переключение листов), то пингани мне
      '$route.params.tenderListId': function (id) {
        this.tenderListId = id;
        this.nestedRowsStructure = null;
        this.hiddenRows = [];
      },
      currentList: {
        handler(val) {
          this.currentRows = val ? [...this.indexRows(val).rows] : [];
        },
        deep: true,
      },
    },
    computed: {
      ...mapState('global', ['currencySymbols']),
      ...mapState('tender', [
        'tenderOffers',
        'tenderInfo',
        'tenderBreakdownRows',
        'refreshTenderList',
        'refreshTenderTableTrigger',
        'tenderLists',
        'offersWithVersions',
        'tenderListsInfoLoading',
      ]),
      ...mapState({
        projectCurrency: (state) => state.global.currentProject.currency,
      }),
      ...mapGetters('global', ['currencyWithSymbol', 'currencySymbol']),
      currentList() {
        let list = null;
        if (this.tenderLists) {
          list = this.tenderLists?.find((elem) => +elem.list?.id === +this.tenderListId);
        }
        return list;
      },
      offers() {
        // TODO: обсудить изменение апишки, чтобы не слать офферы без версий
        return this.offersWithVersions?.filter((offer) => offer.versions.length) || [];
      },
      orderOfRowsIds() {
        let rowsIds = this.currentList.rows
          .filter((row) => !row.row.group_level)
          .map((row) => row.id);
        return rowsIds.filter((rowId) => !this.hiddenRows.includes(rowId));
      },
      dynamicColumnsCount() {
        // Количество столбцов динамически вычисляется в зависимости от
        // количества офферов и факта их подробной демонстрации (раскрытия)
        return (
          this.offers?.length * this.DEFAULT_COLUMN_WIDTH +
          this.openedColumns?.length * this.WIDE_COLUMN_DELTA
        );
      },
      dynamicRowsCount() {
        return this.currentList?.rows?.length;
      },
      computedRows() {
        return this.currentList?.rows;
      },
      footerGridRow() {
        return {
          'grid-row': `${
            this.currentList?.rows?.length * BREAKDOWN_COEFFICIENT + HEADER_ROW_END
          } / ${this.currentList?.rows?.length * BREAKDOWN_COEFFICIENT + FIRST_DYNAMIC_ROW_END}`,
        };
      },
      getGeneralEstimateDropZoneGrid() {
        const styles = {
          'grid-area': `${HEADER_ROW_END} / ${GENERAL_ESTIMATE_COLUMN_START} / ${
            this.currentList?.rows?.length * BREAKDOWN_COEFFICIENT + HEADER_ROW_END
          } / ${DYNAMIC_COLUMNS_START}`,
        };
        if (!this.drag) {
          styles.display = 'none';
        }
        return styles;
      },
      dragOptions() {
        return {
          animation: 200,
          group: 'general-estimate',
          disabled: false,
          scrollSensitivity: 250,
          forceFallback: true,
        };
      },
    },
    methods: {
      ...mapActions('modal', ['openModal']),
      ...mapActions('tender', [
        'loadTenderListsInfo',
        'loadRowBreakdownRows',
        'copyAllPricesToProject',
        'copyCellPricesToProject',
        'copyRowPricesToProject',
        'editGeneralEstimateValue',
      ]),
      ...mapActions({
        confirm: 'confirmModal/openModal',
      }),
      addSpaces,
      get,
      handleInvitation() {
        this.editTender(this.tenderInfo.id, 3);
      },
      editGeneralEstimateValueHandler(cellInfo) {
        const cellInfoLocal = {
          ...cellInfo,
          value: isNaN(+cellInfo.value) ? '0' : (+cellInfo.value).toFixed(2),
        };
        this.editGeneralEstimateValue({
          cellInfo: cellInfoLocal,
          versionIds: this.getVersionIds(),
        });
      },
      getVersionIds() {
        return Object.values(this.selectedVersions).map((elem) => elem.versionId);
      },
      async loadTenderListsInfoHandler() {
        const tenderId = router.currentRoute.params.tenderId;
        const versionIds = this.getVersionIds();
        await this.loadTenderListsInfo({ tenderId, versionIds });
      },
      async changeSelectedVersion(offerId, versionIndex, versionId) {
        this.openedBreakdownRowIds = [];
        const versionIdsBeforeAdd = this.getVersionIds();
        // Обновляем версию и делаем переотрисовку только если выбрана не та же версия
        if (!versionIdsBeforeAdd.includes(versionId)) {
          this.selectedVersions[offerId] = {
            ...this.selectedVersions[offerId],
            versionIndex,
            versionId,
          };
          await this.loadTenderListsInfoHandler();
          // Триггер переотрисовки таблицы, так как дочерние данные не всегда отслеживаются реактивно
          this.versionUpdated = !this.versionUpdated;
        }
      },
      toggleColumn(index) {
        if (this.openedColumns.includes(index)) {
          this.openedColumns = this.openedColumns.filter((elem) => elem !== index);
        } else {
          this.openedColumns.push(index);
        }
      },
      leftSpaceBlocks(index) {
        return this.openedColumns.filter((elem) => elem < index).length * WIDE_COLUMN_DELTA;
      },
      nameColumnStart(index) {
        return index * DEFAULT_COLUMN_WIDTH + DYNAMIC_COLUMNS_START + this.leftSpaceBlocks(index);
      },
      nameColumnEnd(index) {
        return (
          this.nameColumnStart(index) +
          this.openedColumns.includes(index) * WIDE_COLUMN_DELTA +
          DEFAULT_COLUMN_WIDTH
        );
      },
      totalStart(index) {
        return (
          index * DEFAULT_COLUMN_WIDTH +
          (DYNAMIC_COLUMNS_START + 1) +
          this.openedColumns.includes(index) * WIDE_COLUMN_DELTA +
          this.leftSpaceBlocks(index)
        );
      },
      // index принимается для расчета отступа слева, position для порядкового
      // номера внутри столбца оффера
      startDynamicColumnByOrderPosition(index, position) {
        return (
          index * DEFAULT_COLUMN_WIDTH +
          (DYNAMIC_COLUMNS_START + (position - 1)) +
          this.leftSpaceBlocks(index)
        );
      },
      positionGridRow(rowIndex) {
        return {
          'grid-row': `${rowIndex * BREAKDOWN_COEFFICIENT + HEADER_ROW_END} / ${
            rowIndex * BREAKDOWN_COEFFICIENT + FIRST_DYNAMIC_ROW_END
          }`,
        };
      },
      breakdownGridLocation(rowIndex) {
        return {
          'grid-row': `${rowIndex * BREAKDOWN_COEFFICIENT + 1 + HEADER_ROW_END} / ${
            rowIndex * BREAKDOWN_COEFFICIENT + 1 + FIRST_DYNAMIC_ROW_END
          }`,
          'grid-column': `1/${DYNAMIC_COLUMNS_START + this.dynamicColumnsCount}`,
        };
      },
      getHeaderGridByPositionAndIndex(index, position) {
        return {
          'grid-area': `${this.HEADER_ROW_CENTER} / ${this.startDynamicColumnByOrderPosition(
            index,
            position
          )} / ${this.HEADER_ROW_END} / ${
            this.startDynamicColumnByOrderPosition(index, position) + 1
          }`,
        };
      },
      getBodyGridByPositionAndIndex(columnIndex, position, rowIndex) {
        return {
          'grid-area': `${rowIndex * BREAKDOWN_COEFFICIENT + HEADER_ROW_END} /
           ${this.startDynamicColumnByOrderPosition(columnIndex, position)} /
           ${rowIndex * BREAKDOWN_COEFFICIENT + HEADER_ROW_END + 1} /
           ${this.startDynamicColumnByOrderPosition(columnIndex, position) + 1}`,
        };
      },
      getFooterGridByPositionAndIndex(columnIndex, position) {
        return {
          'grid-area': `${this.currentList.rows.length * BREAKDOWN_COEFFICIENT + HEADER_ROW_END} /
           ${this.startDynamicColumnByOrderPosition(columnIndex, position)} /
           ${this.currentList.rows.length * BREAKDOWN_COEFFICIENT + FIRST_DYNAMIC_ROW_END} /
           ${this.startDynamicColumnByOrderPosition(columnIndex, position) + 1}`,
        };
      },
      getFooterTotalGridByPositionAndIndex(columnIndex) {
        return {
          'grid-area': `${this.currentList.rows.length * BREAKDOWN_COEFFICIENT + HEADER_ROW_END} /
           ${this.totalStart(columnIndex)} /
           ${this.currentList.rows.length * BREAKDOWN_COEFFICIENT + FIRST_DYNAMIC_ROW_END} /
           ${this.totalStart(columnIndex) + 1}`,
        };
      },
      getGeneralEstimatePosition(rowIndex, index) {
        return {
          'grid-area': `${rowIndex * BREAKDOWN_COEFFICIENT + HEADER_ROW_END} /
           ${GENERAL_ESTIMATE_COLUMN_START + index} /
           ${rowIndex * BREAKDOWN_COEFFICIENT + HEADER_ROW_END + 1} /
           ${GENERAL_ESTIMATE_COLUMN_START + index + 1}`,
        };
      },
      getTotalFieldGrid(columnIndex, rowIndex) {
        return {
          'grid-area': `${rowIndex * BREAKDOWN_COEFFICIENT + 3} /
          ${this.totalStart(columnIndex)} /
          ${rowIndex * BREAKDOWN_COEFFICIENT + 4} /
          ${this.totalStart(columnIndex) + 1}`,
        };
      },
      openComments(row, rowId, offers) {
        let positionOffers = this.offers.map((offer) => {
          let rowId = null;
          offer.versions.forEach((version) => {
            let versionById = get(offers, version.id, false);
            if (versionById) rowId = versionById.id;
          });
          return {
            team: offer.contractor_team.name,
            row_id: rowId,
          };
        });

        changeQuery({
          position: row.id,
        });

        this.openModal({
          component: TenderPositionCard,
          context: {
            attributes: {
              class: ['create-tender__container'],
            },
            row,
            rowId,
            isIncomingTender: false,
            offers: positionOffers,
            tender: {
              title: get(this.tenderInfo, 'name', ''),
              list: get(this.currentList, 'list.title', ''),
            },
          },
        });
      },
      async fillSelectedVersions() {
        if (this.offers.length) {
          // установка версий через ?version=offerId_versionId
          let versions = this.$route.query.version;
          let versionsMap = new Map();
          let updateList = false;

          if (versions) {
            versions = Array.isArray(versions) ? versions : [versions];
            await deleteQuery(['version']);

            versions.forEach((versionQuery) => {
              let [id, version] = versionQuery.split('_');
              versionsMap.set(+id, +version);
            });
          }

          if (!Object.keys(this.selectedVersions)?.length) {
            this.offers?.forEach((offer, index) => {
              if (offer.versions?.length) {
                if (versionsMap.has(offer.id)) {
                  const versionIndex = offer.versions.findIndex(
                    (version) => version.id === versionsMap.get(offer.id)
                  );
                  this.selectedVersions[offer.id] = {
                    versionIndex,
                    versionId: versionsMap.get(offer.id),
                    versionPriorityIndex: index,
                  };
                } else {
                  if (!(offer.id in this.selectedVersions)) {
                    this.updateList = true;
                    const lastVersion = offer.versions[offer.versions.length - 1];
                    this.selectedVersions[offer.id] = {
                      versionIndex: offer.versions.length - 1,
                      versionId: lastVersion?.id,
                      versionPriorityIndex: index,
                    };
                  }
                }
              }
            });
          }

          // TODO: убрать после рефакторинга
          if (this.versionsHelper || versions || updateList) {
            this.versionsHelper = false;
            await this.loadTenderListsInfoHandler();
          }
        }
      },
      async switchBreakdown(row) {
        if (this.openedBreakdownRowIds.includes(row.id)) {
          const index = this.openedBreakdownRowIds.indexOf(row.id);
          if (index > -1) {
            this.openedBreakdownRowIds.splice(index, 1);
          }
        } else {
          const selectedVersionIds = Object.values(this.selectedVersions)
            ?.map((elem) => elem.versionId)
            .filter((elem) => elem);
          await this.loadRowBreakdownRows({ row, selectedVersionIds });
          this.openedBreakdownRowIds.push(row.id);
        }
      },
      openCommentsByQuery() {
        const positionId = this.$route.query.position;
        if (positionId && this.currentList) {
          let rowByQuery = this.currentList.rows.find((row) => row.row.id === +positionId);
          if (rowByQuery) this.openComments(rowByQuery.row, rowByQuery.id, rowByQuery.offer_rows);
        }
      },
      copyAllPricesToProjectHandler(cellInfo) {
        this.confirm({
          title: this.$t('The old price for this item will be changed after applying this'),
          actionText: this.$t('Yes, change it'),
          cancelText: this.$t('Cancel'),
          description: this.$t(
            `The price from ${cellInfo.offerName} v.${cellInfo.versionName} is now set here`
          ),
          action: () => {
            this.copyAllPricesToProject({
              versionId: cellInfo.id,
              versionIds: this.getVersionIds(),
            });
          },
        });
      },
      dragStartHandler() {
        this.drag = true;
      },
      dragEndHandler(e, offerId, cellInfo, isTotal = false) {
        // Проверяем также дочерние теги (должно быть не -1)
        const dropZoneIndex = e.originalEvent?.path?.findIndex(
          (path) => path.id === this.GENERAL_ESTIMATE_DROP_ZONE_ID
        );
        // если дроп был над зоной general estimate, то обновляем ячейку general estimate
        if (~dropZoneIndex) {
          const selectedVersionId = this.selectedVersions[offerId].versionId;
          if (isTotal) {
            this.copyRowPricesToProject({ versionId: selectedVersionId, ...cellInfo });
          } else {
            this.copyCellPricesToProject({ versionId: selectedVersionId, ...cellInfo });
          }
        }
        this.drag = false;
      },
    },
    beforeDestroy() {
      this.selectedVersions = {};
      this.openedColumns = [];
    },
  };
</script>
